/**
 * MaintenanceScheduler Component - Phase X4
 * Modal for scheduling/editing aircraft maintenance
 */

import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Label } from '../ui/label';
import { Alert, AlertDescription } from '../ui/alert';
import { Badge } from '../ui/badge';
import { Wrench, AlertTriangle, Calendar, Clock, X } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import api from '../../lib/api';
import dayjs from 'dayjs';

const MAINTENANCE_TYPES = ['scheduled', 'unscheduled', 'calibration', 'repair', 'inspection'];

// Snap datetime to 15-min boundary
const snapTo15Min = (dateStr) => {
  if (!dateStr) return '';
  const d = dayjs(dateStr);
  const minute = Math.floor(d.minute() / 15) * 15;
  return d.minute(minute).second(0).format('YYYY-MM-DDTHH:mm');
};

export const MaintenanceScheduler = ({
  isOpen,
  onClose,
  aircraftId,
  maintenance = null, // null for create, object for edit
  onSuccess
}) => {
  const { t, language } = useLanguage();
  const tr = t.aircraft?.maintenance || {};
  const sch = tr.scheduler || {};
  const types = tr.types || {};
  
  const isEdit = !!maintenance;
  
  // Form state
  const [formData, setFormData] = useState({
    start_at: '',
    end_at: '',
    type: 'scheduled',
    reason: '',
    technician: '',
    notes: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [conflicts, setConflicts] = useState(null);
  
  // Initialize form on open/maintenance change
  useEffect(() => {
    if (isOpen) {
      if (maintenance) {
        setFormData({
          start_at: maintenance.start_at ? dayjs(maintenance.start_at).format('YYYY-MM-DDTHH:mm') : '',
          end_at: maintenance.end_at ? dayjs(maintenance.end_at).format('YYYY-MM-DDTHH:mm') : '',
          type: maintenance.type || 'scheduled',
          reason: maintenance.reason || '',
          technician: maintenance.technician || '',
          notes: maintenance.notes || ''
        });
      } else {
        // Default: tomorrow 09:00 to 17:00
        const tomorrow = dayjs().add(1, 'day');
        setFormData({
          start_at: tomorrow.hour(9).minute(0).format('YYYY-MM-DDTHH:mm'),
          end_at: tomorrow.hour(17).minute(0).format('YYYY-MM-DDTHH:mm'),
          type: 'scheduled',
          reason: '',
          technician: '',
          notes: ''
        });
      }
      setError(null);
      setConflicts(null);
    }
  }, [isOpen, maintenance]);
  
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
    setConflicts(null);
  };
  
  // Snap on blur
  const handleDateBlur = (field) => {
    const snapped = snapTo15Min(formData[field]);
    if (snapped !== formData[field]) {
      setFormData(prev => ({ ...prev, [field]: snapped }));
    }
  };
  
  const handleSubmit = async (force = false) => {
    setLoading(true);
    setError(null);
    
    try {
      const endpoint = isEdit 
        ? `/aircraft/${aircraftId}/maintenance/${maintenance.id}?lang=${language}` 
        : `/aircraft/${aircraftId}/maintenance?lang=${language}${force ? '&force=true' : ''}`;
      
      const body = {
        start_at: dayjs(formData.start_at).toISOString(),
        end_at: dayjs(formData.end_at).toISOString(),
        type: formData.type,
        reason: formData.reason,
        technician: formData.technician || null,
        notes: formData.notes || null
      };
      
      const response = isEdit 
        ? await api.patch(endpoint, body)
        : await api.post(endpoint, body);
      
      onSuccess?.(response.data);
      onClose();
    } catch (err) {
      const errorData = err.response?.data;
      if (errorData?.error === 'maintenance_conflicts_with_missions') {
        setConflicts(errorData);
        return;
      }
      setError(language === 'cs' ? errorData?.message_cs : errorData?.message_en || err.message || 'Error');
    } finally {
      setLoading(false);
    }
  };
  
  const isValid = formData.start_at && formData.end_at && formData.reason?.length >= 5 && 
    dayjs(formData.end_at).isAfter(dayjs(formData.start_at));
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wrench className="h-5 w-5 text-gray-600" />
            {isEdit ? sch.editTitle : sch.createTitle}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Date/Time inputs */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="start_at" className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                {sch.startAt}
              </Label>
              <Input
                id="start_at"
                type="datetime-local"
                value={formData.start_at}
                onChange={(e) => handleChange('start_at', e.target.value)}
                onBlur={() => handleDateBlur('start_at')}
                data-testid="maintenance-start"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="end_at" className="flex items-center gap-1">
                <Clock className="h-4 w-4" />
                {sch.endAt}
              </Label>
              <Input
                id="end_at"
                type="datetime-local"
                value={formData.end_at}
                onChange={(e) => handleChange('end_at', e.target.value)}
                onBlur={() => handleDateBlur('end_at')}
                min={formData.start_at}
                data-testid="maintenance-end"
              />
            </div>
          </div>
          
          {/* Type selector */}
          <div className="space-y-2">
            <Label>{sch.type}</Label>
            <Select value={formData.type} onValueChange={(v) => handleChange('type', v)}>
              <SelectTrigger data-testid="maintenance-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MAINTENANCE_TYPES.map(type => (
                  <SelectItem key={type} value={type}>
                    {types[type] || type}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Reason */}
          <div className="space-y-2">
            <Label htmlFor="reason">{sch.reason} *</Label>
            <Textarea
              id="reason"
              value={formData.reason}
              onChange={(e) => handleChange('reason', e.target.value)}
              placeholder={sch.reasonPlaceholder}
              rows={2}
              data-testid="maintenance-reason"
            />
            {formData.reason && formData.reason.length < 5 && (
              <p className="text-xs text-red-500">Min. 5 {language === 'cs' ? 'znaků' : 'characters'}</p>
            )}
          </div>
          
          {/* Technician */}
          <div className="space-y-2">
            <Label htmlFor="technician">{sch.technician}</Label>
            <Input
              id="technician"
              value={formData.technician}
              onChange={(e) => handleChange('technician', e.target.value)}
              placeholder={sch.technicianPlaceholder}
              data-testid="maintenance-technician"
            />
          </div>
          
          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">{sch.notes}</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              placeholder={sch.notesPlaceholder}
              rows={2}
              data-testid="maintenance-notes"
            />
          </div>
          
          {/* Error display */}
          {error && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          
          {/* Conflicts display */}
          {conflicts && (
            <Alert className="border-amber-500 bg-amber-50 dark:bg-amber-900/20">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-800 dark:text-amber-200">
                <p className="font-medium mb-2">{tr.conflicts?.title}</p>
                <p className="text-sm mb-2">{tr.conflicts?.message}</p>
                <div className="space-y-1">
                  {conflicts.conflicting_missions?.map(m => (
                    <div key={m.mission_id} className="flex items-center gap-2 text-xs">
                      <Badge variant="outline" className="font-mono">{m.mission_code}</Badge>
                      <span>{m.mission_name}</span>
                      <span className="text-gray-500">
                        {dayjs(m.etot).format('DD.MM HH:mm')} - {dayjs(m.eldt).format('HH:mm')}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2 mt-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setConflicts(null)}
                  >
                    {tr.conflicts?.cancelCreation}
                  </Button>
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => handleSubmit(true)}
                    className="bg-amber-600 hover:bg-amber-700"
                  >
                    {tr.conflicts?.forceCreate}
                  </Button>
                </div>
              </AlertDescription>
            </Alert>
          )}
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            {sch.cancel}
          </Button>
          <Button 
            onClick={() => handleSubmit(false)} 
            disabled={!isValid || loading || !!conflicts}
            data-testid="maintenance-submit"
          >
            {loading ? '...' : sch.submit}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MaintenanceScheduler;
